// Trading Toast Hook - specialized trading notifications
import { useNotification } from '../context/NotificationContext';
import { getApiErrorMessage } from '../utils/apiErrorHandler';

export const useTradingToast = () => {
  const { toast: baseToast } = useNotification();

  const toast = {
    // =============== BASIC TOASTS ===============
    success: (title, message) => {
      baseToast.success(title, message);
    },
    
    error: (title, message) => {
      baseToast.error(title, message);
    },
    
    warning: (title, message) => {
      baseToast.warning(title, message);
    },
    
    info: (title, message) => {
      baseToast.info(title, message);
    },

    // =============== TRADING SPECIFIC ===============
    
    // Position opened successfully
    positionOpened: (symbol, side, leverage, margin) => {
      const direction = side === 'long' ? '🟢 Long' : '🔴 Short';
      baseToast.success(
        `${direction} открыт`,
        `${symbol} • ${leverage}x • $${margin.toFixed(2)}`
      );
    },

    // Position closed
    positionClosed: (symbol, pnl) => {
      const isProfitable = pnl >= 0;
      const emoji = isProfitable ? '💰' : '📉';
      const sign = isProfitable ? '+' : '';
      
      if (isProfitable) {
        baseToast.success(
          `${emoji} Позиция закрыта`,
          `${symbol} • PnL: ${sign}$${Math.abs(pnl).toFixed(2)}`
        );
      } else {
        baseToast.warning(
          `${emoji} Позиция закрыта`,
          `${symbol} • PnL: ${sign}$${Math.abs(pnl).toFixed(2)}`
        );
      }
    },

    // Limit order placed
    limitOrder: (symbol, side, price) => {
      const direction = side === 'long' ? '🟢 Long' : '🔴 Short';
      baseToast.info(
        `📋 Лимитный ордер`,
        `${symbol} ${direction} @ $${price.toFixed(2)}`
      );
    },

    // Order cancelled
    orderCancelled: (symbol) => {
      baseToast.info(
        '❌ Ордер отменён',
        `${symbol}`
      );
    },

    // Spot buy
    buy: (symbol, amount) => {
      baseToast.success(
        '🛒 Покупка выполнена',
        `+${amount.toFixed(6)} ${symbol}`
      );
    },

    // Spot sell
    sell: (symbol, amount, pnl) => {
      const isProfitable = pnl >= 0;
      const sign = isProfitable ? '+' : '';
      baseToast.success(
        '💵 Продажа выполнена',
        `${symbol} • PnL: ${sign}$${Math.abs(pnl).toFixed(2)}`
      );
    },

    // =============== VALIDATION ERRORS ===============
    
    validationError: (message) => {
      baseToast.warning('⚠️ Проверьте данные', message);
    },

    insufficientFunds: () => {
      baseToast.error('💳 Недостаточно средств', 'Пополните баланс для торговли');
    },

    priceUnavailable: () => {
      baseToast.warning('📊 Цена недоступна', 'Попробуйте позже или выберите другую пару');
    },

    invalidAmount: () => {
      baseToast.warning('⚠️ Введите сумму', 'Укажите корректную сумму для сделки');
    },

    // =============== API ERRORS ===============
    
    apiError: (err, defaultMsg = 'Ошибка выполнения операции') => {
      const message = getApiErrorMessage(err, defaultMsg);
      baseToast.error('❌ Ошибка', message);
    },

    // =============== SYSTEM STATUS ===============
    
    wsConnected: () => {
      baseToast.success('🔗 Подключено', 'WebSocket соединение установлено');
    },

    wsDisconnected: () => {
      baseToast.warning('⚠️ Отключено', 'Соединение потеряно, переподключение...');
    },

    // =============== TRADE EVENTS ===============
    
    takeProfit: (symbol, pnl) => {
      baseToast.success(
        '🎯 Take Profit',
        `${symbol} закрыт с прибылью +$${pnl.toFixed(2)}`
      );
    },

    stopLoss: (symbol, pnl) => {
      baseToast.warning(
        '🛑 Stop Loss',
        `${symbol} закрыт с убытком -$${Math.abs(pnl).toFixed(2)}`
      );
    },

    liquidation: (symbol, loss) => {
      baseToast.error(
        '⚠️ Ликвидация',
        `${symbol} ликвидирован. Потеря: $${Math.abs(loss).toFixed(2)}`
      );
    }
  };

  return { toast };
};

export default useTradingToast;
